import {
  AnalyticsViewType,
  ExperienceDevicePreviewDto,
  PatchExperienceResponseBodyType,
} from "@intelligems/ig-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import {
  exclusionGroupKeys,
  experienceKeys,
  invalidateModuleExperienceListQueries,
  setExperienceQueryData,
} from "../queryKeys";

export type PatchExperienceParams = {
  id: string;
  name?: string;
  analyticsViewType?: AnalyticsViewType;
  devicePreview?: ExperienceDevicePreviewDto;
  isPreview?: boolean;
};

export type UsePatchExperienceOptions = {
  /** Invalidate all list caches after a successful patch (e.g. after a rename). */
  invalidateLists?: boolean;
  onSuccess?: (data: PatchExperienceResponseBodyType) => void;
  onError?: () => void;
};

export const patchExperience = async ({
  id,
  name,
  analyticsViewType,
  devicePreview,
  isPreview,
}: PatchExperienceParams): Promise<PatchExperienceResponseBodyType> => {
  const resp = await api.patch<PatchExperienceResponseBodyType>(
    `/v2/experiences/${id}`,
    { id, name, analyticsViewType, devicePreview, isPreview }
  );
  return resp.data;
};

export const usePatchExperience = (
  experienceId?: string,
  options?: UsePatchExperienceOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["mutatePatchExperience"],
    mutationFn: (data: PatchExperienceParams) => patchExperience(data),
    onSuccess: (data) => {
      if (experienceId) {
        setExperienceQueryData(queryClient, data.experience);
      }
      if (options?.invalidateLists) {
        invalidateModuleExperienceListQueries(queryClient);

        if (data.experience.category === "experiment") {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.experimentList,
          });
        } else {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.detailedPersonalizationList,
          });
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.homeOverviewPersonalizationList,
          });
        }
        void queryClient.invalidateQueries({
          queryKey: exclusionGroupKeys.all,
        });
      }
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};
